'use client';

import { useEffect, useRef, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';

import { WidgetsApp } from '@/components';
import { Types } from '@scorenco/core';

import {
  LiveWidget,
  RankingWidget,
  TeamWidget,
  PreviousNextWidget,
  EventsListWidget,
  CompetitionWidget,
  WeekEventsWidget,
  PlayersWidget,
} from '../../components/Widgets';
import { GetWidgetMembersByOldIdDocument } from './_internal/Widget.query.generated';

interface OldWidgetData {
  __typename: 'widgets_widget';
  id: number;
  category: string;
  team_detail_list?: Types.Teams_Team_Detail[];
  team_settings?: {
    show_ranking?: boolean;
    show_events?: boolean;
    show_name_in_club?: boolean;
    nb_events_to_show?: number;
    display_priority?: string;
  };
  live_settings?: Types.Widgets_Widget_Live | null;
  content_club_id?: number;
  club_id?: number;
  sort?: string;
  day_switch_tab?: string;
  events_away_home?: string;
  settings?: { show_name_in_club?: boolean };
  widget_competitions?: { competition_id: number }[];
}

const OldWidget = () => {
  const router = useRouter();
  const hasTrackedRef = useRef(false);
  const [widget, setWidget] = useState<OldWidgetData | null>(null);

  // Récupération du widget via API REST
  useEffect(() => {
    const fetchWidget = async () => {
      if (!router.query.oldWidgetId) {
        return;
      }

      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_GRAPHQL_API_ENDPOINT_CACHED}/widget/by-old-id/${router.query.oldWidgetId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setWidget(data.widgets_widget?.[0] || null);
        } else {
          console.error(
            'Erreur lors de la récupération du widget:',
            response.status
          );
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du widget:', error);
      }
    };

    fetchWidget();
  }, [router.query.oldWidgetId]);

  // Tracking pour les anciens widgets
  useEffect(() => {
    if (hasTrackedRef.current) return;
    if (typeof window === 'undefined') return;
    if (!widget?.id) return;

    hasTrackedRef.current = true;

    // N'envoie que si on peut identifier la page parente (hors iframe)
    let referrer = typeof document !== 'undefined' ? document.referrer : '';
    if (
      !referrer &&
      typeof window !== 'undefined' &&
      (window as unknown as { location: { ancestorOrigins?: DOMStringList } })
        .location?.ancestorOrigins?.length
    ) {
      try {
        const origin = (
          window as unknown as { location: { ancestorOrigins: DOMStringList } }
        ).location.ancestorOrigins[0];
        if (origin) referrer = origin;
      } catch {
        void 0;
      }
    }
    if (!referrer) return;

    let domainName = '';
    try {
      const parsed = new URL(referrer);
      domainName = parsed.hostname;
    } catch {
      return; // si le referrer est invalide, on n'envoie pas
    }
    const pageUrl = referrer;

    // Ignore le tracking si l'URL parent est un domaine scorenco (prod/preprod/dev)
    if (pageUrl.includes('scorenco.com')) {
      return;
    }

    const payload: { domain_name: string; url: string; widget_id: number } = {
      domain_name: domainName,
      url: pageUrl,
      widget_id: widget.id,
    };

    try {
      void fetch(
        `https://${process.env.NEXT_PUBLIC_REST_ENDPOINT}/widgets/track/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
          mode: 'cors',
        }
      );
    } catch (_) {
      // ignore
    }
  }, [widget?.id]);

  const getMembers = useQuery(GetWidgetMembersByOldIdDocument, {
    variables: {
      oldWidgetId: router.query.oldWidgetId as string,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !router.query.oldWidgetId || widget?.category !== 'players',
    context: {
      role: 'anonymous',
    },
  });

  const renderWidget = () => {
    if (!widget) return null;

    switch (widget.category) {
      case 'ranking':
        return (
          <RankingWidget
            teams={
              widget.team_detail_list as Types.Teams_Team_Detail[] | undefined
            }
          />
        );
      case 'team':
        return (
          <TeamWidget
            teams={widget.team_detail_list}
            settings={widget.team_settings}
          />
        );
      case 'live':
        return (
          <LiveWidget
            widgetTeamId={widget.team_detail_list?.[0]?.team_id}
            liveSettings={widget.live_settings}
            clubId={widget.content_club_id || widget?.club_id}
          />
        );
      case 'last_next_games':
        return (
          <PreviousNextWidget
            teams={widget.team_detail_list}
            settings={widget?.team_settings}
          />
        );
      case 'previous-next':
        return (
          <PreviousNextWidget
            teams={widget.team_detail_list}
            settings={widget?.team_settings}
          />
        );
      case 'events_list':
        return (
          <EventsListWidget
            teams={widget.team_detail_list}
            clubId={widget.content_club_id || widget?.club_id}
            sort={widget.sort}
            settings={widget?.team_settings}
          />
        );
      case 'events-list':
        return (
          <EventsListWidget
            teams={widget.team_detail_list}
            clubId={widget.content_club_id || widget?.club_id}
            sort={widget.sort}
            settings={widget?.team_settings}
          />
        );
      case 'competition':
        return (
          <CompetitionWidget
            competition_id={widget.widget_competitions?.[0]?.competition_id}
          />
        );
      case 'week_events':
        return (
          <WeekEventsWidget
            teams={widget.team_detail_list}
            clubId={widget.content_club_id || widget?.club_id}
            sort={widget.sort}
            daySwitch={widget.day_switch_tab}
            eventsAwayHome={widget.events_away_home}
            settings={widget?.settings}
          />
        );
      case 'week-events':
        return (
          <WeekEventsWidget
            teams={widget.team_detail_list}
            clubId={widget.content_club_id || widget?.club_id}
            sort={widget.sort}
            daySwitch={widget.day_switch_tab}
            eventsAwayHome={widget.events_away_home}
            settings={widget?.settings}
          />
        );
      case 'players':
        return (
          <PlayersWidget
            members={
              getMembers.data?.widgets_widget?.[0]?.widget_teams?.[0]
                ?.team_detail?.[0]?.team_in_season?.members
            }
          />
        );
      default:
        return null;
    }
  };

  // OldWidgetData is a legacy REST API structure that mirrors Widgets_Widget but with simplified
  // sub-types (e.g. settings without __typename). The cast is safe since the data comes from
  // the widgets_widget endpoint and has the same runtime shape.
  return (
    <WidgetsApp
      widget={
        (widget ?? undefined) as unknown as Types.Widgets_Widget | undefined
      }
    >
      {renderWidget()}
    </WidgetsApp>
  );
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? 'fr', [
      'snc-components',
      'snc-core',
      'common',
    ])),
  },
});

export default OldWidget;
